import React from "react";
import {useTranslation} from "react-i18next";
import {CompanyCardDetail, CompanyCardField} from "../../app/api/model/organization/companyCard";
import {ColumnDef, FilterDef, FilterOperator, ListRequestIdModelTable} from "../../components/ExTable";
import Page from "../../components/Layout/Page";
import {DateFormat, formatTimestamp} from "../../utils/datetime";
import {useCompanyCardListAllQuery} from "../../app/api/api";
import {OrganizationField} from "../../app/api/model/organization";

export default function CompanyCardsTable() {
    const {t} = useTranslation();

    const columnDefs: ColumnDef<CompanyCardDetail>[] = [
        {
            fieldName: CompanyCardField.CARD_ID,
            align: "left",
            label: t("companyCard_cardId"),
            width: 100,
            sortable: true,
            sticky: true,
        },
        {
            fieldName: CompanyCardField.ORGANIZATION_NAME,
            align: "left",
            label: t("companyCard_assignedOrganization"),
            width: 150,
            sortable: true,
            formatCellText: ({row}) => row[CompanyCardField.ORGANIZATION]?.[OrganizationField.NAME],
        },
        {
            fieldName: OrganizationField.MAIN_DKV_CUSTOMER_NUMBER,
            align: "left",
            label: t("companyCard_assignedCustomer"),
            width: 150,
            sortable: true,
            formatCellText: ({row}) => row[CompanyCardField.ORGANIZATION]?.[OrganizationField.MAIN_DKV_CUSTOMER_NUMBER],
        },
        {
            fieldName: CompanyCardField.VALID_UNTIL,
            align: "left",
            label: t("companyCard_validUntil"),
            width: 180,
            sortable: true,
            formatCellText: ({cellValue}) => formatTimestamp(cellValue, DateFormat.DATE),
        },
        {
            fieldName: CompanyCardField.CARD_READER_SLOT,
            align: "left",
            label: t("companyCard_cardReaderSlot"),
            width: 180,
            sortable: true,
            formatCellText: ({cellValue}) => cellValue,
        },
        {
            fieldName: CompanyCardField.CARD_READER_DRAWER,
            align: "left",
            label: t("companyCard_cardReaderDrawer"),
            width: 180,
            sortable: true,
            formatCellText: ({cellValue}) => cellValue,
        },
    ];

    const filterDefs: FilterDef<CompanyCardDetail>[] = [
        {
            fieldName: CompanyCardField.CARD_ID,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ, FilterOperator.IN],
        },
        {
            fieldName: CompanyCardField.ORGANIZATION_NAME,
            filterOperators: [
                FilterOperator.CONTAINS,
                FilterOperator.STARTSWITH,
                FilterOperator.EQ,
                FilterOperator.IN,
                FilterOperator.NEX,
            ],
        },
        {
            fieldName: OrganizationField.MAIN_DKV_CUSTOMER_NUMBER,
            filterOperators: [
                FilterOperator.CONTAINS,
                FilterOperator.STARTSWITH,
                FilterOperator.EQ,
                FilterOperator.IN,
                FilterOperator.NEX,
            ],
        },
        {
            fieldName: CompanyCardField.VALID_UNTIL,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.GTE,
                FilterOperator.LTE,
                FilterOperator.GT,
                FilterOperator.LT,
            ],
        },
        {
            fieldName: CompanyCardField.CARD_READER_SLOT,
            filterOperators: [FilterOperator.EQ, FilterOperator.NEX],
        },
        {
            fieldName: CompanyCardField.CARD_READER_DRAWER,
            filterOperators: [FilterOperator.EQ, FilterOperator.NEX],
        },
    ];

    return (
        <Page>
            <ListRequestIdModelTable<CompanyCardDetail>
                dataTest={"companyCardsList"}
                queryHook={useCompanyCardListAllQuery}
                columnDefs={columnDefs}
                filterDefs={filterDefs}
                title={t("generic_companyCards")}
                selectableRows={false}
            />
        </Page>
    );
}
